import { useMemo } from 'react'
import { useAppSelector } from 'hooks/reduxHooks'
import offerPageURL from 'lib/offer/offerPageURL'
import { getSuggestedDates } from 'selectors/offerSelectors'
import { FLEXIBLE_DURATION_RANGE } from 'constants/search'
import { getImmersiveTripId } from 'tripPlanner/selectors'
import config from 'constants/config'
import { buildSearchParamsFromFilters, buildSuggestedDatesParamsKey, convertFlexibleDatesToTravelDates, encodeChildrenOccupancy } from 'lib/search/searchUtils'
import { OFFER_TYPE_BED_BANK } from 'constants/offer'
import { SOURCE_TYPES } from 'constants/source'
import { queryKeySource, urlHashDatePicker, queryKeyAdults, queryKeyChildren } from 'constants/url'
import useOfferMetaData from './useOfferMetaData'
import { OfferUnavailableReason } from '../../lib/search/constants'
import { isLEHotel } from '../../lib/offer/offerTypes'

interface Options {
  filters?: App.OfferListFilters
  bundledOfferId?: string
  offerLinkIncludesFilters?: boolean
  searchId?: string
  showDatePicker?: boolean
  utmBp?: string
}

function useOfferUrl(offer: App.AnyOffer, options: Options): string
function useOfferUrl(offer: App.AnyOffer | undefined, options: Options): string | undefined
function useOfferUrl(offer: App.AnyOffer | undefined, options: Options): string | undefined {
  const offerId = offer?.id ?? ''
  const { filters, bundledOfferId, offerLinkIncludesFilters, showDatePicker } = options
  const flexibleSearchFilterKey = buildSuggestedDatesParamsKey(filters?.flexibleMonths, filters?.flexibleNights, filters?.rooms)
  const suggestedDates = useAppSelector(state => getSuggestedDates(state, flexibleSearchFilterKey, offerId))
  const tripId = useAppSelector(state => getImmersiveTripId(state))
  const searchMetadata = useOfferMetaData(offerId, options.filters)

  const isPropertySearchTile = useMemo(() => {
    return (isLEHotel(offer) && filters?.propertyId && !filters?.searchNearby && offer.property?.id === filters.propertyId.split(':')[1])
  }, [filters, offer])

  const overrideOccupancy: Array<App.SearchOccupancy> = useMemo(() => searchMetadata?.rooms.map(room => room.occupancy) ?? [], [searchMetadata])

  return useMemo(() => {
    if (!offer) return undefined

    let offerUrlSuggestedDates: App.OfferSuggestedDates | undefined = suggestedDates
    if (!suggestedDates?.checkIn && !suggestedDates?.checkOut && filters?.flexibleNights && filters?.flexibleMonths) {
      const { checkIn, checkOut } = convertFlexibleDatesToTravelDates(filters.flexibleNights as FLEXIBLE_DURATION_RANGE, filters.flexibleMonths)
      offerUrlSuggestedDates = {
        ...suggestedDates,
        checkIn,
        checkOut,
      }
    }

    const noDatesInUrl =
      (!!isPropertySearchTile && searchMetadata?.unavailableReason === OfferUnavailableReason.NUM_NIGHTS_UNSUPPORTED) || showDatePicker

    const searchParams = offerLinkIncludesFilters ? buildSearchParamsFromFilters(filters, offerUrlSuggestedDates, { noDatesInUrl }) : new URLSearchParams()
    if (config.OPEN_NEW_TAB_OFFER_CLICK && tripId) {
      // Make sure immersive trip header persists when opening offer links in new tab
      searchParams.set('tripId', tripId)
    }
    if (bundledOfferId) {
      searchParams.set('bundledOfferId', bundledOfferId)
    }

    const departureMonths = filters?.departureMonths?.filter(Boolean) ?? []
    if (departureMonths.length > 0) {
      departureMonths.forEach(month => {
        searchParams.append('departureMonths', month)
      })
    }

    if (offer.type === OFFER_TYPE_BED_BANK && filters?.source === SOURCE_TYPES.METASEARCH) {
      searchParams.set(queryKeySource, filters.source)
    }

    if (options.searchId) {
      searchParams.set('searchId', options.searchId)
    }

    if (options.utmBp) {
      searchParams.set('utm_bp', options.utmBp)
    }

    // If there's an override occupancy defined, and it's not empty, use it to set the occupancy params
    if (overrideOccupancy.length > 0) {
      const adults = overrideOccupancy.map(room => room.numberOfAdults).join(',')

      const children = overrideOccupancy.map(room => encodeChildrenOccupancy(room.agesOfChildren)).join(',')

      // Set the occupancy params
      searchParams.set(queryKeyAdults, adults)
      searchParams.set(queryKeyChildren, children)
    }

    return offerPageURL(offer, searchParams, showDatePicker ? urlHashDatePicker : undefined)
  }, [
    suggestedDates,
    filters,
    isPropertySearchTile,
    searchMetadata?.unavailableReason,
    offerLinkIncludesFilters,
    tripId,
    bundledOfferId,
    offer,
    options.searchId,
    showDatePicker,
    options.utmBp,
    overrideOccupancy,
  ])
}

export default useOfferUrl
